import {
  AmplifyDelete,
  AmplifyGet,
  AmplifyPatch,
  AmplifyPost,
} from "@sunrun/sales-experience-shared/amplify-api-wrapper";
import { type SplatProspectUpdate } from "../../amplify/backend/function/OfferExpUsage/ts/public/types";
import {
  CancelSiteSurveyAppointmentRequestData,
  CombinedEnvelopeEnabledResponse,
  DesignPreferencesResponse,
  UpdateDesignPreferencesRequestBody,
  UpdateDesignPreferencesResponse,
  RescheduleSiteSurveyAppointmentRequestData,
  SiteSurveySlotRequestData,
  SplatCheckoutDocumentStatusResponse,
  SplatCheckoutDocumentType,
} from "../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { listOffers } from "./storeFront";
import { subscribeToAsyncRequest } from "utils/subscribeToAsyncRequest";
import { Attachment } from "utils/usageUtils";
import { retry } from "utils/async";
import { logger } from "providers/logger";

const patchUsage = async (
  jwt: string,
  prospectId: string,
  prospectData: Partial<SplatProspectUpdate>
): Promise<{
  asyncRequestId: string;
}> => {
  return await AmplifyPatch(
    "Patch Usage",
    "OfferExpApi",
    `/prospect/${prospectId}/usage`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: prospectData,
    },
    logger
  );
};

const postAttachment = async (
  jwt: string,
  prospectId: string,
  attachment: Attachment
): Promise<any> => {
  const formData = new FormData();
  formData.append("documentBlob", attachment.documentBlob);
  formData.append("prospectId", prospectId);
  formData.append("contentType", attachment.contentType);
  formData.append("documentName", attachment.documentName);
  formData.append("documentType", attachment.documentType);

  return AmplifyPost(
    "Post Attachment",
    "OfferExpApi",
    `/prospect/${prospectId}/usage/attachments`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": `multipart/form-data`,
      },
      body: formData,
    },
    logger
  ).catch((err) => {
    const status = err?.response?.status || "";
    if (status === 408) {
      console.log(
        "initial attempt timed out, it will probably still go through anyways"
      );
      return;
    }
    throw err;
  });
};

const deleteAttachment = async (
  jwt: string,
  prospectId: string,
  attachmentId: string
): Promise<void> => {
  await AmplifyDelete(
    "Delete Attachment",
    "OfferExpApi",
    `/prospect/${prospectId}/usage/attachments/${attachmentId}`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const postCheckoutAttachment = async (
  authKey: string,
  prospectId: string,
  proposalId: string,
  attachment: Attachment
): Promise<any> => {
  const formData = new FormData();
  formData.append("documentBlob", attachment.documentBlob);
  formData.append("prospectId", prospectId);
  formData.append("proposalId", proposalId);
  formData.append("contentType", attachment.contentType);
  formData.append("documentName", attachment.documentName);
  formData.append("documentType", attachment.documentType);
  const attachmentUrlsAsyncResponse = await AmplifyPost<{
    asyncRequestId: string;
  }>(
    "Attachment Urls",
    "OfferExpApi",
    `/prospect/${prospectId}/proposals/${proposalId}/get-attachment-urls`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
        "Content-Type": `multipart/form-data`,
      },
      body: formData,
    },
    logger
  );
  const attachmentUrls = await subscribeToAsyncRequest(
    authKey,
    attachmentUrlsAsyncResponse.asyncRequestId
  );

  formData.append("uploadUrls", JSON.stringify(attachmentUrls));

  const { url: uploadUrl } = await AmplifyPost<{ url: string }>(
    "Upload Attachment Blob",
    "OfferExpApi",
    `/prospect/${prospectId}/proposals/${proposalId}/upload-attachment-blob`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
        "Content-Type": `multipart/form-data`,
      },
      body: formData,
    },
    logger
  );

  formData.append("uploadUrl", uploadUrl);

  const checkoutAttachmentAsyncResponse = await AmplifyPost<{
    asyncRequestId: string;
  }>(
    "Upload Attachment",
    "OfferExpApi",
    `/prospect/${prospectId}/proposals/${proposalId}/upload-attachment`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
        "Content-Type": `multipart/form-data`,
      },
      body: formData,
    },
    logger
  );

  return await subscribeToAsyncRequest(
    authKey,
    checkoutAttachmentAsyncResponse.asyncRequestId
  );
};

const postCreditCheck = (
  jwt: string,
  prospectId: string,
  contactId: string,
  action: string,
  isCustomer?: boolean
): Promise<{
  url?: string;
  success?: boolean;
  message?: string;
}> => {
  return AmplifyPost(
    `${isCustomer ? "Customer" : ""} Credit Check`,
    "OfferExpApi",
    `${isCustomer ? "/customer" : ""}/prospect/${prospectId}/creditCheck`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        contactId,
        action: action,
      },
    },
    logger
  );
};

const postLoanApplicationEmail = (
  jwt: string,
  prospectId: string,
  proposalId: string,
  coBorrowerId?: string | undefined
): Promise<any> => {
  let url = `/prospect/${prospectId}/proposal/${proposalId}/loan/application/send-email`;
  if (coBorrowerId) {
    url += `?coborrower=${coBorrowerId}`;
  }
  return AmplifyPost(
    "Send Loan Application Email",
    "OfferExpApi",
    url,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const postPaymentRequestEmail = async (
  jwt: string,
  prospectId: string,
  paymentMode: string
): Promise<any> => {
  let url = `/prospect/${prospectId}/sendPaymentRequestEmail`;
  let body = {
    paymentMode: paymentMode,
  };
  return AmplifyPost(
    "Send Payment Request Email",
    "OfferExpApi",
    url,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body,
    },
    logger
  );
};

const createSiteSurveyAppointment = async (
  authKey: string,
  prospectId: string,
  requestData: SiteSurveySlotRequestData
) => {
  const createSSApptAsyncResponse = await AmplifyPost<{
    asyncRequestId: string;
  }>(
    "Create Site Survey Appointment",
    "OfferExpApi",
    `/prospect/${prospectId}/site-survey/appointment`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: requestData,
    },
    logger
  );

  return await subscribeToAsyncRequest(
    authKey,
    createSSApptAsyncResponse.asyncRequestId
  );
};

const cancelSiteSurveyAppointment = async (
  authKey: string,
  prospectId: string,
  appointmentId: string,
  requestData: CancelSiteSurveyAppointmentRequestData
) => {
  const cancelSSApptAsyncResponse = await AmplifyPatch<{
    asyncRequestId: string;
  }>(
    "Cancel Site Survey Appointment",
    "OfferExpApi",
    `/prospect/${prospectId}/site-survey/appointment/${appointmentId}/cancel-site-survey`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: requestData,
    },
    logger
  );

  return await subscribeToAsyncRequest(
    authKey,
    cancelSSApptAsyncResponse.asyncRequestId
  );
};

const rescheduleSiteSurveyAppointment = async (
  authKey: string,
  requestData: RescheduleSiteSurveyAppointmentRequestData
) => {
  const rescheduleSSApptAsyncResponse = await AmplifyPost<{
    asyncRequestId: string;
  }>(
    "Reschedule Site Survey Appointment",
    "OfferExpApi",
    `/reschedule-site-survey`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: requestData,
    },
    logger
  );

  return await subscribeToAsyncRequest(
    authKey,
    rescheduleSSApptAsyncResponse.asyncRequestId
  );
};

const sendIpaEmail = async (
  jwt: string,
  prospectId: string,
  proposalId: string,
  isCustomer?: boolean
) => {
  return await AmplifyGet<{
    status: string;
  }>(
    `${isCustomer ? "Customer " : ""}Send IPA Email`,
    "OfferExpApi",
    `${
      isCustomer ? "/customer" : ""
    }/prospect/${prospectId}/proposals/${proposalId}/sendIlIpaEmail`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

type SalesRepUser = {
  contactId: string;
  phone: string;
  email: string;
  firstName: string;
  lastName: string;
};

const getCustomerSalesRep = async (proposalToken: string | undefined) => {
  return await AmplifyGet<SalesRepUser>(
    "Get Customer Sales Rep",
    "OfferExpApi",
    `/customer/${proposalToken}/salesRep`,
    {},
    logger
  );
};

const sendLoanEmail = async (
  jwt: string,
  prospectId: string,
  proposalId: string,
  secondaryContactId?: string,
  isCustomer?: boolean
) => {
  const sendLoanEmailResponse = await AmplifyPost<{
    status: string;
  }>(
    `${isCustomer ? "Customer " : ""}Send Loan Email`,
    "OfferExpApi",
    `${
      isCustomer ? "/customer" : ""
    }/prospect/${prospectId}/proposals/${proposalId}/send-loan-email`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        secondaryContactId,
      },
    },
    logger
  );
  if (sendLoanEmailResponse.status === "FAILED") {
    throw new Error("Error sending loan document email.");
  }
  return sendLoanEmailResponse;
};

const signDocument = async (
  props: {
    authKey: string;
    prospectId: string;
    proposalId: string;
    documentType?: SplatCheckoutDocumentType;
    eSignatureSource: string;
    eSignatureType: string;
    primaryContactId: string;
    /** Combined envelope flow (send all documents in one signing experience). */
    combinedAgreement?: boolean;
  },
  secondaryContactId?: string,
  action?: string,
  isCustomer?: boolean
) => {
  const {
    authKey,
    prospectId,
    proposalId,
    documentType,
    eSignatureSource,
    eSignatureType,
    primaryContactId,
    combinedAgreement = false,
  } = props;
  const signDocumentAsyncResponse = await AmplifyPost<{
    asyncRequestId: string;
  }>(
    `${isCustomer ? "Customer " : ""}Sign Document`,
    "OfferExpApi",
    `${
      isCustomer ? "/customer" : ""
    }/prospect/${prospectId}/proposals/${proposalId}/sign-document`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: {
        action,
        eSignatureType,
        primaryContactId,
        secondaryContactId,
        eSignatureSource,
        ...(combinedAgreement
          ? { combinedAgreement: true, proposalId }
          : {
              documentType,
            }),
      },
    },
    logger
  );

  return await subscribeToAsyncRequest(
    authKey,
    signDocumentAsyncResponse.asyncRequestId,
    isCustomer
  );
};

const regenDocs = async ({
  authKey,
  prospectId,
}: {
  authKey: string;
  prospectId: string;
}): Promise<any> => {
  return await AmplifyPost<{
    asyncRequestId: string;
  }>(
    "Regen Docs",
    "OfferExpApi",
    `/regen-docs/prospect/${prospectId}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
    },
    logger
  );
};

const generateAdHocDocument = async ({
  authKey,
  proposalId,
  documentType,
}: {
  authKey: string;
  proposalId: string;
  documentType: SplatCheckoutDocumentType;
}): Promise<any> => {
  return AmplifyPost(
    "Generate Ad Hoc Document",
    "OfferExpApi",
    `/generate-ad-hoc-document/${proposalId}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: {
        documentType,
      },
    },
    logger
  ).catch((err) => {
    const status = err?.response?.status || "";
    console.log("error status", status);
    throw err;
  });
};

const voidProposal = async ({
  authKey,
  prospectId,
  proposalId,
  cognitoToken,
  identity,
}: {
  authKey: string;
  prospectId: string;
  proposalId: string;
  identity: string;
  cognitoToken: string;
}): Promise<any> => {
  await AmplifyDelete(
    "Void Proposal",
    "OfferExpApi",
    `/void-proposals/prospect/${prospectId}/proposals/${proposalId}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
    },
    logger
  );

  return retry({
    fn: async () => listOffers(cognitoToken, prospectId, identity),
    test: (value: unknown) => {
      return (
        Array.isArray(value) && !value.find((offer) => offer.id === proposalId)
      );
    },
  });
};

const voidAllProposals = async ({
  authKey,
  prospectId,
  identity,
  cognitoToken,
}: {
  authKey: string;
  prospectId: string;
  identity: string;
  cognitoToken: string;
}): Promise<any> => {
  await AmplifyDelete(
    "Void All Proposals",
    "OfferExpApi",
    `/void-proposals/prospect/${prospectId}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
    },
    logger
  );
  return retry({
    fn: async () => listOffers(cognitoToken, prospectId, identity),
    test: (offers: unknown) => {
      return Array.isArray(offers) && offers?.length === 0;
    },
  });
};

const voidDocument = async (
  props: {
    authKey: string;
    prospectId: string;
    proposalId: string;
    documentType: SplatCheckoutDocumentType;
    eSignatureSource: string;
    eSignatureType: string;
    primaryContactId: string;
    isCustomer?: boolean;
  },
  action?: string
) => {
  const {
    authKey,
    prospectId,
    proposalId,
    documentType,
    eSignatureSource,
    eSignatureType,
    primaryContactId,
    isCustomer,
  } = props;
  const voidDocumentAsyncResponse = await AmplifyPost<{
    asyncRequestId: string;
  }>(
    `${isCustomer ? "Customer " : ""}Void Documents`,
    "OfferExpApi",
    `${
      isCustomer ? "/customer" : ""
    }/prospect/${prospectId}/proposals/${proposalId}/void-document`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: {
        action,
        documentType,
        eSignatureSource,
        eSignatureType,
        primaryContactId,
      },
    },
    logger
  );

  return await subscribeToAsyncRequest(
    authKey,
    voidDocumentAsyncResponse.asyncRequestId,
    isCustomer
  );
};

const getDocumentStatus = async (props: {
  authKey: string;
  prospectId: string;
  proposalId: string;
  documentType: SplatCheckoutDocumentType;
  isCustomer?: boolean;
}): Promise<SplatCheckoutDocumentStatusResponse> => {
  const { authKey, prospectId, proposalId, documentType, isCustomer } = props;
  return await AmplifyGet(
    "Get Document Status",
    "OfferExpApi",
    `${
      isCustomer ? "/customer" : ""
    }/prospect/${prospectId}/proposals/${proposalId}/document-status?documentType=${documentType}`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
    },
    logger
  );
};

const combinedEnvelopeEnabled = async (props: {
  authKey: string;
  prospectId: string;
  proposalId: string;
  isCustomer?: boolean;
}): Promise<CombinedEnvelopeEnabledResponse> => {
  const { authKey, prospectId, proposalId, isCustomer } = props;
  const combinedEnvelopeEnabledAsyncResponse = await AmplifyPost<{
    asyncRequestId: string;
  }>(
    `${isCustomer ? "Customer " : ""} Post - Combined Envelope Enabled`,
    "OfferExpApi",
    `${
      isCustomer ? "/customer" : ""
    }/prospect/${prospectId}/proposals/${proposalId}/sign-document`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body: {
        action: "combinedEnvelopeEnabled",
        proposalId,
      },
    },
    logger
  );

  return await subscribeToAsyncRequest(
    authKey,
    combinedEnvelopeEnabledAsyncResponse.asyncRequestId,
    isCustomer
  );
};

const getDesignPreferences = async (props: {
  authKey: string;
  prospectId: string;
}): Promise<DesignPreferencesResponse> => {
  const { authKey, prospectId } = props;
  return await AmplifyGet<DesignPreferencesResponse>(
    "Get Design Preferences",
    "OfferExpApi",
    `/prospect/${prospectId}/designPreferences`,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
    },
    logger
  );
};

const updateDesignPreferences = async (
  authKey: string,
  prospectId: string,
  body: UpdateDesignPreferencesRequestBody
): Promise<UpdateDesignPreferencesResponse> => {
  let url = `/prospect/${prospectId}/designPreferences`;
  return AmplifyPost(
    "Update Design Preferences",
    "OfferExpApi",
    url,
    {
      headers: {
        Authorization: `Bearer ${authKey}`,
      },
      body,
    },
    logger
  );
};

export type { SalesRepUser };
export {
  cancelSiteSurveyAppointment,
  rescheduleSiteSurveyAppointment,
  createSiteSurveyAppointment,
  getDocumentStatus,
  combinedEnvelopeEnabled,
  getCustomerSalesRep,
  patchUsage,
  postAttachment,
  deleteAttachment,
  getDesignPreferences,
  updateDesignPreferences,
  postCheckoutAttachment,
  postCreditCheck,
  postLoanApplicationEmail,
  postPaymentRequestEmail,
  sendIpaEmail,
  sendLoanEmail,
  signDocument,
  voidAllProposals,
  voidDocument,
  voidProposal,
  regenDocs,
  generateAdHocDocument,
};
